/**
 * Public read-only Wage Report view — opened via a secure portal token
 * sent to the client over email. No login required; the token is the
 * secret.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Download } from "lucide-react";
import { api, API_BASE } from "@/lib/api";

const nz = (v, dp = 0) => v == null ? "—" : `$${Number(v).toLocaleString("en-NZ", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;

export default function WageReportView() {
    const { token } = useParams();
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/portal/wage-reports/token/${token}`)
            .then(({ data }) => setDoc(data))
            .catch((e) => setErr(e?.response?.data?.detail || "Report not found"))
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center text-[#5E636E]">
            <Loader2 className="h-5 w-5 animate-spin" />
        </div>
    );
    if (err) return (
        <div className="min-h-screen bg-[#F5F4EF] flex items-center justify-center p-6">
            <div className="rounded-md border border-[#E4E4E7] bg-white p-8 max-w-md text-center">
                <h1 className="text-xl font-semibold text-[#0F1115]">Report not available</h1>
                <p className="text-[13px] text-[#5E636E] mt-2">{err}</p>
            </div>
        </div>
    );

    const r = doc.result || {};
    const primary = doc.firm_branding?.primary_color || "#0F1115";

    const status = r.adjustment_status || "unknown";
    const adj = Number(r.recommended_adjustment_weekly || 0);
    const palette = status === "below_affordable_level"
        ? { border: "#059669", chip: "bg-emerald-50 text-emerald-900 border-emerald-200", num: "text-emerald-700", label: "ROOM TO INCREASE OWNER PAY" }
        : status === "above_affordable_level"
            ? { border: "#DC2626", chip: "bg-red-50 text-red-900 border-red-200", num: "text-red-700", label: "OWNER PAY ABOVE CURRENT AFFORDABLE LEVEL" }
            : status === "within"
                ? { border: "#0284C7", chip: "bg-sky-50 text-sky-900 border-sky-200", num: "text-sky-700", label: "WITHIN AFFORDABLE RANGE" }
                : { border: primary, chip: "bg-zinc-50 text-[#0F1115] border-[#E4E4E7]", num: "text-[#0F1115]", label: "Owner pay comparison not available" };
    const sign = adj > 0 ? "+" : adj < 0 ? "−" : "";
    const breakdown = Array.isArray(r.owner_breakdown) ? r.owner_breakdown : [];

    return (
        <div className="min-h-screen bg-[#F5F4EF] py-10 px-6" data-testid="portal-wage-report">
            <div className="max-w-3xl mx-auto">
                <div className="flex items-start justify-between gap-3 mb-6">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[#5E636E]">Prepared by</div>
                        <div className="text-lg font-semibold mt-0.5" style={{ color: primary }}>{doc.firm_name}</div>
                    </div>
                    <a href={`${API_BASE}/portal/wage-reports/token/${token}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-[12.5px] hover:bg-zinc-50" data-testid="portal-download-pdf">
                        <Download className="h-3.5 w-3.5" /> Download PDF
                    </a>
                </div>

                <div className="rounded-lg bg-[#F7F6F2] border border-[#E4E4E7] p-6 mb-6">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#5E636E]">Owner Wage Report</div>
                    <h1 className="text-2xl font-bold text-[#0F1115] mt-1">{doc.client_name}</h1>
                    <div className="text-[12.5px] text-[#5E636E] mt-1">
                        Sent {doc.sent_at ? new Date(doc.sent_at).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" }) : "recently"}
                        {doc.version > 1 ? ` · Version ${doc.version}` : ""}
                    </div>
                </div>

                <div className="rounded-lg bg-white border-2 p-6 mb-6" style={{ borderColor: palette.border }}>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#5E636E]">Owner pay position</div>
                    <div className="mt-3 grid grid-cols-2 gap-4">
                        <div>
                            <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">Affordable owner pay</div>
                            <div className="text-2xl font-bold text-[#0F1115] tabular-nums mt-1">{nz(r.total_affordable_weekly || r.affordable_weekly)}/wk</div>
                            <div className="text-[11.5px] text-[#5E636E]">Annual {nz(r.total_affordable_annual || r.affordable_annual)}</div>
                        </div>
                        <div>
                            <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">Currently being paid</div>
                            <div className="text-2xl font-bold text-[#0F1115] tabular-nums mt-1">{nz(r.total_current_owner_pay_weekly || 0)}/wk</div>
                            <div className="text-[11.5px] text-[#5E636E]">Annual {nz(r.total_current_owner_pay_annual || 0)}</div>
                        </div>
                    </div>
                    <div className="mt-5 pt-4 border-t border-[#EEEDE7]">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[#5E636E]">Recommended adjustment</div>
                        <div className={`text-5xl font-bold tabular-nums mt-2 ${palette.num}`} data-testid="portal-recommended-adjustment">
                            {sign}{nz(Math.abs(adj))}/wk
                        </div>
                        <div className={`inline-flex mt-2 rounded-full border px-3 py-1 text-[11px] font-semibold ${palette.chip}`}>
                            {palette.label}
                        </div>
                    </div>
                    {r.owner_count > 1 && breakdown.length > 1 && (
                        <div className="mt-5 pt-4 border-t border-[#EEEDE7]">
                            <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E] mb-2">Per owner (total split across {r.owner_count})</div>
                            <ul className="text-[13px] text-[#1F2328] space-y-1 tabular-nums">
                                {breakdown.map((o, i) => (
                                    <li key={i} className="flex justify-between">
                                        <span>{o.name || `Owner ${i + 1}`} · {o.allocation_pct}%</span>
                                        <span><b>{nz(o.affordable_weekly)}</b>/wk · adjust {o.adjustment_weekly >= 0 ? "+" : "−"}{nz(Math.abs(o.adjustment_weekly))}/wk</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="rounded-lg bg-white border border-[#E4E4E7] p-6 mb-6">
                    <h2 className="font-semibold text-[#0F1115] mb-3">How we got there</h2>
                    <ul className="text-[13px] text-[#1F2328] space-y-2">
                        <li className="flex justify-between border-b border-[#EEEDE7] pb-2"><span>Net profit per P&amp;L</span><b>{nz(r.net_profit_per_pl)}</b></li>
                        <li className="flex justify-between border-b border-[#EEEDE7] pb-2"><span>+ Owner remuneration added back</span><b>{nz((doc.adjustments || {}).addback_owner_wages || 0)}</b></li>
                        <li className="flex justify-between border-b border-[#EEEDE7] pb-2"><span>Profit before owner pay</span><b>{nz(r.adjusted_net_profit)}</b></li>
                        <li className="flex justify-between border-b border-[#EEEDE7] pb-2"><span>Affordable weekly (what the business can pay)</span><b>{nz(r.total_affordable_weekly || r.affordable_weekly)}</b></li>
                        <li className="flex justify-between border-b border-[#EEEDE7] pb-2"><span>Fair market weekly (benchmark)</span><b>{nz(r.fair_weekly)}</b></li>
                        <li className="flex justify-between pt-2"><span>Confidence</span><b className="capitalize">{r.confidence || "moderate"}</b></li>
                    </ul>
                </div>

                <div className="rounded-lg bg-white border border-[#E4E4E7] p-6 mb-6 text-[13px] text-[#1F2328] leading-relaxed">
                    <h2 className="font-semibold text-[#0F1115] mb-2">What this means</h2>
                    <p>Your <b>affordable owner pay</b> is an estimate of what the business can currently support based on the financial information provided.</p>
                    <p className="mt-2">If owner wages are already included in your Profit &amp; Loss statement, we add those amounts back when assessing the performance of the business, and then compare the calculated affordable pay with what you are already receiving.</p>
                    <p className="mt-2">The <b>recommended adjustment</b> is the important number. It shows whether your current pay may be increased, maintained, or reduced.</p>
                </div>

                {doc.commentary && (
                    <div className="rounded-lg bg-white border border-[#E4E4E7] p-6 mb-6">
                        <h2 className="font-semibold text-[#0F1115] mb-2">Your accountant's notes</h2>
                        <div className="text-[13px] text-[#1F2328] whitespace-pre-wrap">{doc.commentary}</div>
                    </div>
                )}

                <div className="text-[11.5px] text-[#5E636E] mt-10 pt-6 border-t border-[#E4E4E7] leading-relaxed">
                    This report is a management estimate based on the financial information provided. It is designed to help with owner-pay and cash-flow decisions and is not a guarantee that the calculated amount will always be available. Tax obligations, GST, PAYE, debt repayments, capital expenditure, seasonal changes and other cash requirements may affect the amount available to withdraw or pay as wages. Reply directly to the email from {doc.firm_name} with any questions.
                </div>
            </div>
        </div>
    );
}
